/**
 * useCacheExplorer Hook
 *
 * Reads live Redis cache data from CCMS.Api.System: cache statistics (auto-refreshed),
 * a SCAN-based key browser, and read-only inspection of a key's stored value.
 *
 * Note: httpClient.get<T> already wraps the payload in ResponseData<T>, so the
 * generic argument is the bare payload type and the body is read from response.data.
 */

import { useState, useCallback } from 'react';
import { useHttpClient } from '@actschurch/ccms-sdk';
import { getApiUrl } from '../../../../config/ccms';
import type { CacheStatistics, KeyScanResult, CacheKeyValue } from '../types';

export function useCacheExplorer() {
  const httpClient = useHttpClient();
  const [stats, setStats] = useState<CacheStatistics | null>(null);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const response = await httpClient.get<CacheStatistics>(
        getApiUrl('system', '/api/redismanagement/statistics')
      );
      setStats(response.data ?? null);
      setStatsError(null);
    } catch (err: any) {
      if (err?.message?.includes('404') || err?.message?.includes('Not Found')) {
        setStatsError(
          'Redis statistics endpoint not available. Start the CCMS.Api.System backend or route /system/* to it.'
        );
      } else {
        setStatsError(err?.message || 'Failed to load Redis statistics');
      }
    } finally {
      setLoading(false);
    }
  }, [httpClient]);

  const scanKeys = useCallback(
    async (pattern: string, pageSize = 200): Promise<KeyScanResult> => {
      const effectivePattern = pattern.trim() || '*';
      const response = await httpClient.get<KeyScanResult>(
        getApiUrl(
          'system',
          `/api/redismanagement/keys/scan?pattern=${encodeURIComponent(effectivePattern)}&pageSize=${pageSize}`
        )
      );
      return response.data ?? { cursor: 0, keys: [], hasMore: false };
    },
    [httpClient]
  );

  const getKeyValue = useCallback(
    async (key: string): Promise<CacheKeyValue> => {
      const response = await httpClient.get<CacheKeyValue>(
        getApiUrl('system', `/api/redismanagement/keys/value/${encodeURIComponent(key)}`)
      );
      return (
        response.data ?? {
          key,
          type: 'None',
          ttlSeconds: null,
          sizeBytes: 0,
          exists: false,
          truncated: false,
          value: '',
        }
      );
    },
    [httpClient]
  );

  return { stats, statsError, loading, fetchStats, scanKeys, getKeyValue };
}
